import DOMPurify from 'isomorphic-dompurify';
import { useMemo } from 'react';

interface RichTextRendererProps {
  html: string;
  className?: string;
}

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'hr',
  'span', 'div',
];

const ALLOWED_ATTR = ['style', 'class'];

/**
 * Detects if a string is HTML produced by the RichTextEditor.
 * Falls back to rendering plain text (with line breaks preserved) if not.
 */
function isHtmlContent(value: string): boolean {
  return /<\/?(p|h[1-6]|ul|ol|li|strong|em|u|br|hr|span|div)(\s|>)/i.test(value);
}

export function RichTextRenderer({ html, className = '' }: RichTextRendererProps) {
  const sanitized = useMemo(() => {
    if (!html) return '';
    if (!isHtmlContent(html)) {
      // Plain text — preserve line breaks
      const escaped = html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<p>${escaped.replace(/\n/g, '<br />')}</p>`;
    }
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
    });
  }, [html]);

  if (!sanitized) return null;

  return (
    <div
      className={`rich-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
